import { useState, useRef } from "react";

const T = {
  bg:"#0B0F1A", surface:"#111827", card:"#1A2235", border:"#1E2D45",
  accent:"#38BDF8", green:"#34D399", yellow:"#FCD34D", red:"#F87171",
  purple:"#A78BFA", orange:"#FB923C", pink:"#F472B6",
  textPri:"#F0F6FF", textSec:"#94A3B8", textMute:"#4B5563",
};

/* ── Graph helpers ───────────────────────────────────────── */
const RG=8, UG=25, CG=220;
const gx = x => CG + x*UG;
const gy = y => CG - y*UG;

function fmt(n) {
  const r = Math.round(n*100)/100;
  return Math.abs(r)<0.005 ? "0" : String(r);
}

function edgePoints(a, b) {
  const pts = [];
  const add = (x, y) => {
    if (!pts.some(p => Math.abs(p[0]-x)<1e-6 && Math.abs(p[1]-y)<1e-6)) pts.push([x,y]);
  };
  [-RG, RG].forEach(xx => { const yy=a*xx+b; if(yy>=-RG-1e-9&&yy<=RG+1e-9) add(xx,yy); });
  if (a!==0) [-RG, RG].forEach(yy => { const xx=(yy-b)/a; if(xx>=-RG-1e-9&&xx<=RG+1e-9) add(xx,yy); });
  return pts;
}

/* ── Function Machine (hero widget) ─────────────────────── */
function FunctionMachine() {
  const [xVal, setXVal] = useState(4);
  const y = 2*xVal+3;
  const shown = xVal<0 ? `(${xVal})` : String(xVal);
  const box = (style={}) => ({
    borderRadius:6, padding:"12px 10px", fontFamily:"monospace",
    fontSize:18, fontWeight:700, textAlign:"center", ...style,
  });
  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:16}}>
      <div style={{fontSize:11,letterSpacing:".1em",textTransform:"uppercase",color:T.textMute,textAlign:"center",marginBottom:6}}>Input</div>
      <div style={box({border:`1.5px dashed ${T.border}`,color:T.textPri})}>x = {xVal}</div>
      <div style={{color:T.textMute,fontSize:20,textAlign:"center",margin:"4px 0"}}>↓</div>
      <div style={box({background:T.surface,border:`1.5px solid ${T.textPri}`,color:T.textPri})}>y = 2x + 3</div>
      <div style={{color:T.textMute,fontSize:20,textAlign:"center",margin:"4px 0"}}>↓</div>
      <div style={{fontSize:11,letterSpacing:".1em",textTransform:"uppercase",color:T.textMute,textAlign:"center",marginBottom:6}}>Output</div>
      <div style={box({border:`1.5px solid ${T.orange}`,color:T.orange})}>y = {y}</div>
      <div style={{fontFamily:"monospace",fontSize:13,color:T.textSec,textAlign:"center",margin:"10px 0 4px"}}>
        2 × {shown} + 3 = {y}
      </div>
      <div style={{display:"flex",alignItems:"center",gap:10,margin:"12px 0 2px"}}>
        <label style={{fontFamily:"monospace",fontSize:12,letterSpacing:".06em",textTransform:"uppercase",color:T.textMute,whiteSpace:"nowrap"}}>Input x</label>
        <input type="range" min="-6" max="6" step="1" value={xVal}
          onChange={e=>setXVal(Number(e.target.value))}
          style={{flex:1,accentColor:T.orange}}/>
        <span style={{fontFamily:"monospace",fontSize:15,fontWeight:700,color:T.orange,minWidth:32,textAlign:"right"}}>{xVal}</span>
      </div>
      <p style={{fontFamily:"monospace",fontSize:11,color:T.textMute,textAlign:"center",margin:"8px 0 0"}}>
        Slide the input and watch the machine work
      </p>
    </div>
  );
}

/* ── Graph Explorer ──────────────────────────────────────── */
function GraphExplorer() {
  const [a, setA] = useState(2);
  const [b, setB] = useState(1);

  const gridLines = [];
  for (let i=-RG; i<=RG; i++){
    const maj = i%5===0;
    gridLines.push(
      <line key={`v${i}`} x1={gx(i)} y1={gy(-RG)} x2={gx(i)} y2={gy(RG)} stroke={maj?"#1E3A5F":"#141E2E"} strokeWidth={maj?.8:.4}/>,
      <line key={`h${i}`} x1={gx(-RG)} y1={gy(i)} x2={gx(RG)} y2={gy(i)} stroke={maj?"#1E3A5F":"#141E2E"} strokeWidth={maj?.8:.4}/>
    );
  }

  const refPts = edgePoints(1, 0);
  const pts = edgePoints(a, b);

  let eq = "y = ";
  if (a===0) eq += fmt(b);
  else {
    eq += (a===1?"x":(a===-1?"−x":fmt(a)+"x"));
    if (b>0) eq += " + "+fmt(b);
    else if (b<0) eq += " − "+fmt(Math.abs(b));
  }

  const direction = a>0?"Rising":(a<0?"Falling":"Horizontal");
  const model = a>0?"Linear growth":(a<0?"Linear decay":"No change");
  const diffStr = a>0?"+"+fmt(a):fmt(a);

  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 270px",gap:16,marginTop:20,alignItems:"start"}}>
      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:12}}>
        <svg viewBox="0 0 440 440" style={{display:"block",width:"100%"}}>
          {gridLines}
          <line x1={gx(-RG)} y1={CG} x2={gx(RG)} y2={CG} stroke={T.textMute} strokeWidth={1.4}/>
          <line x1={CG} y1={gy(-RG)} x2={CG} y2={gy(RG)} stroke={T.textMute} strokeWidth={1.4}/>
          {[-5,5].map(v=>(
            <g key={v}>
              <text x={gx(v)} y={CG+13} textAnchor="middle" fontSize={9} fill={T.textMute} fontFamily="monospace">{v}</text>
              <text x={CG-7} y={gy(v)+3.5} textAnchor="end" fontSize={9} fill={T.textMute} fontFamily="monospace">{v}</text>
            </g>
          ))}
          <text x={gx(RG)-4} y={CG-9} textAnchor="end" fontSize={10} fill={T.textSec} fontFamily="monospace">x</text>
          <text x={CG+8} y={gy(RG)+11} fontSize={10} fill={T.textSec} fontFamily="monospace">y</text>
          {refPts.length>=2&&(
            <line x1={gx(refPts[0][0])} y1={gy(refPts[0][1])} x2={gx(refPts[1][0])} y2={gy(refPts[1][1])}
              stroke={T.border} strokeWidth={1.6} strokeDasharray="5 4"/>
          )}
          {pts.length>=2&&(
            <line x1={gx(pts[0][0])} y1={gy(pts[0][1])} x2={gx(pts[1][0])} y2={gy(pts[1][1])}
              stroke={T.orange} strokeWidth={2.6} strokeLinecap="round"/>
          )}
          {b>=-RG&&b<=RG&&(
            <>
              <circle cx={CG} cy={gy(b)} r={5} fill={T.accent}/>
              <text x={CG+10} y={gy(b)-9} fontSize={10.5} fontWeight={700} fill={T.accent} fontFamily="monospace">(0, {fmt(b)})</text>
            </>
          )}
        </svg>
        <div style={{display:"flex",alignItems:"center",gap:10,margin:"10px 0 4px"}}>
          <label style={{fontFamily:"monospace",fontSize:12,letterSpacing:".06em",textTransform:"uppercase",color:T.textMute,whiteSpace:"nowrap"}}>Slope a</label>
          <input type="range" min="-3" max="3" step="0.5" value={a}
            onChange={e=>setA(Number(e.target.value))} style={{flex:1,accentColor:T.orange}}/>
          <span style={{fontFamily:"monospace",fontSize:15,fontWeight:700,color:T.orange,minWidth:40,textAlign:"right"}}>{fmt(a)}</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,margin:"6px 0 2px"}}>
          <label style={{fontFamily:"monospace",fontSize:12,letterSpacing:".06em",textTransform:"uppercase",color:T.textMute,whiteSpace:"nowrap"}}>Intercept b</label>
          <input type="range" min="-6" max="6" step="1" value={b}
            onChange={e=>setB(Number(e.target.value))} style={{flex:1,accentColor:T.orange}}/>
          <span style={{fontFamily:"monospace",fontSize:15,fontWeight:700,color:T.orange,minWidth:40,textAlign:"right"}}>{fmt(b)}</span>
        </div>
        <p style={{fontFamily:"monospace",fontSize:11,color:T.textMute,textAlign:"center",margin:"8px 0 0"}}>
          Dashed grey line = y = x (reference)
        </p>
      </div>

      <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:16}}>
        <div style={{fontSize:11,letterSpacing:.7,textTransform:"uppercase",color:T.textMute,fontWeight:700,marginBottom:8}}>The line right now</div>
        <div style={{fontFamily:"monospace",fontSize:20,fontWeight:700,color:T.accent,margin:"4px 0 14px"}}>{eq}</div>
        {[["cuts y-axis at",`(0, ${fmt(b)})`],["direction",direction],["models",model]].map(([l,v])=>(
          <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"7px 0",
            borderBottom:`1px solid ${T.border}`,fontFamily:"monospace",fontSize:13}}>
            <span style={{color:T.textMute}}>{l}</span>
            <span style={{color:T.textPri}}>{v}</span>
          </div>
        ))}
        <div style={{overflowX:"auto",marginTop:12}}>
          <table style={{borderCollapse:"collapse",width:"100%",fontFamily:"monospace",fontSize:13}}>
            <thead>
              <tr style={{background:T.surface}}>
                <th style={{border:`1px solid ${T.border}`,padding:"5px 8px",color:T.textMute,textAlign:"center"}}>x</th>
                {[0,1,2,3].map(k=><th key={k} style={{border:`1px solid ${T.border}`,padding:"5px 8px",color:T.textMute,textAlign:"center"}}>{k}</th>)}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{border:`1px solid ${T.border}`,padding:"5px 8px",color:T.textPri,textAlign:"center"}}>y</td>
                {[0,1,2,3].map(k=><td key={k} style={{border:`1px solid ${T.border}`,padding:"5px 8px",color:T.textSec,textAlign:"center"}}>{fmt(a*k+b)}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{fontFamily:"monospace",fontSize:11,color:T.textMute,marginTop:10,lineHeight:1.5}}>
          Constant difference between consecutive y-values: {diffStr}
        </div>
      </div>
    </div>
  );
}

/* ── UI helpers ──────────────────────────────────────────── */
function Callout({type="info",tag,children}){
  const color=type==="warn"?T.yellow:type==="trick"?T.purple:T.accent;
  return(
    <div style={{background:T.card,borderLeft:`3px solid ${color}`,padding:"14px 18px",
      margin:"18px 0",borderRadius:"0 6px 6px 0",border:`1px solid ${T.border}`,borderLeftWidth:3,borderLeftColor:color}}>
      {tag&&<div style={{fontFamily:"monospace",fontSize:11,letterSpacing:".1em",textTransform:"uppercase",color,marginBottom:6}}>{tag}</div>}
      <div style={{fontSize:14,color:T.textSec,lineHeight:1.65}}>{children}</div>
    </div>
  );
}

function Formula({children,style={}}){
  return(
    <div style={{fontFamily:"monospace",fontSize:"clamp(13px,1.8vw,16px)",background:T.card,
      border:`1px dashed ${T.border}`,padding:"15px 18px",textAlign:"center",margin:"16px 0",
      borderRadius:6,color:T.textPri,overflowX:"auto",...style}}>
      {children}
    </div>
  );
}

function WorkedEx({title,question,steps,answer}){
  return(
    <div style={{background:T.card,border:`1px solid ${T.border}`,padding:"16px 20px",borderRadius:8,margin:"18px 0"}}>
      <div style={{fontSize:14,fontWeight:700,color:T.textPri,marginBottom:4}}>{title}</div>
      <div style={{fontSize:13.5,color:T.textSec,fontStyle:"italic",marginBottom:10}}>{question}</div>
      <ol style={{paddingLeft:0,margin:0,listStyle:"none"}}>
        {steps.map((s,i)=>(
          <li key={i} style={{display:"flex",gap:10,margin:"7px 0",fontSize:13.5,color:T.textSec,lineHeight:1.65}}>
            <span style={{flexShrink:0,width:21,height:21,border:`1px solid ${T.border}`,borderRadius:"50%",
              display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"monospace",fontSize:11,color:T.orange,marginTop:2}}>{i+1}</span>
            <span>{s}</span>
          </li>
        ))}
      </ol>
      {answer&&<div style={{borderTop:`1px dashed ${T.border}`,marginTop:10,paddingTop:8,fontWeight:600,fontSize:14,color:T.textPri}}>{answer}</div>}
    </div>
  );
}

function DataTable({headers,rows}){
  return(
    <div style={{overflowX:"auto",margin:"16px 0"}}>
      <table style={{borderCollapse:"collapse",width:"100%",background:T.card,fontSize:13.5}}>
        <thead>
          <tr style={{background:T.surface}}>
            {headers.map((h,i)=>(
              <th key={i} style={{border:`1px solid ${T.border}`,padding:"8px 12px",textAlign:"left",
                color:T.textMute,fontSize:12,letterSpacing:".05em",fontWeight:600}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row,ri)=>(
            <tr key={ri}>
              {row.map((cell,ci)=>(
                <td key={ci} style={{border:`1px solid ${T.border}`,padding:"8px 12px",color:T.textSec}}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PracticeQ({no,marks,question,solution}){
  const [open,setOpen]=useState(false);
  return(
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,marginBottom:10,overflow:"hidden"}}>
      <div onClick={()=>setOpen(!open)}
        style={{cursor:"pointer",padding:"13px 18px",display:"flex",gap:12,alignItems:"flex-start"}}>
        <span style={{fontFamily:"monospace",fontSize:12,color:T.orange,flexShrink:0,paddingTop:2}}>{no}</span>
        <span style={{fontSize:14.5,color:T.textPri,flex:1,lineHeight:1.6}}>{question}</span>
        <span style={{fontFamily:"monospace",fontSize:11,color:T.textMute,border:`1px solid ${T.border}`,
          padding:"2px 7px",borderRadius:4,flexShrink:0}}>{marks}m</span>
        <span style={{color:T.textMute,fontSize:12}}>{open?"▲":"▼"}</span>
      </div>
      {open&&(
        <div style={{padding:"12px 18px 16px 44px",borderTop:`1px dashed ${T.border}`}}>
          <div style={{fontSize:11,letterSpacing:".1em",textTransform:"uppercase",color:T.green,fontWeight:700,marginBottom:8}}>Solution</div>
          <div style={{fontSize:13.5,color:T.textSec,lineHeight:1.7}}>{solution}</div>
        </div>
      )}
    </div>
  );
}

function Cd({children}){
  return<code style={{fontFamily:"monospace",fontSize:".9em",color:T.accent,
    background:"rgba(56,189,248,0.08)",padding:"1px 5px",borderRadius:4}}>{children}</code>;
}
function SL({n}){return<div style={{fontFamily:"monospace",fontSize:11,letterSpacing:".12em",textTransform:"uppercase",color:T.orange,marginBottom:8}}>Topic {String(n).padStart(2,"0")}</div>;}
function H2({c}){return<h2 style={{fontSize:"clamp(22px,3vw,30px)",fontWeight:800,color:T.textPri,margin:"0 0 12px",lineHeight:1.1}}>{c}</h2>;}
function H3({c}){return<h3 style={{fontSize:16,fontWeight:700,color:T.textPri,margin:"22px 0 7px"}}>{c}</h3>;}
function P({c}){return<p style={{fontSize:14.5,color:T.textSec,lineHeight:1.7,maxWidth:"66ch",margin:"9px 0"}}>{c}</p>;}

/* ── Sidebar nav ─────────────────────────────────────────── */
const NAV=[
  {id:"expressions", label:"Expressions",    icon:"📝"},
  {id:"degree",      label:"Degree",          icon:"🎓"},
  {id:"linear",      label:"Linear Poly",     icon:"📐"},
  {id:"patterns",    label:"Patterns",        icon:"🔢"},
  {id:"growth",      label:"Growth & Decay",  icon:"📈"},
  {id:"relationship",label:"y = ax + b",      icon:"🔗"},
  {id:"graphs",      label:"Graphs",          icon:"📊"},
  {id:"mistakes",    label:"Mistakes",        icon:"⚠️"},
  {id:"practice",    label:"Practice",        icon:"✏️"},
  {id:"summary",     label:"Summary",         icon:"📋"},
];

const SS={paddingBottom:44,borderBottom:`1px solid #1E2D45`,marginBottom:36};

export default function Class9MathsCh2(){
  const [active,setActive]=useState("expressions");
  const mainRef=useRef(null);
  const refs=useRef({});
  const SR=id=>el=>{refs.current[id]=el;};

  function go(id){
    setActive(id);
    const el=refs.current[id], box=mainRef.current;
    if(el&&box) box.scrollTop=el.offsetTop-16;
  }

  return(
    <div style={{display:"flex",height:"calc(100vh - 70px)",background:T.bg,fontFamily:"'Inter',sans-serif"}}>

      {/* Sidebar */}
      <div style={{width:196,flexShrink:0,background:T.surface,borderRight:`1px solid ${T.border}`,display:"flex",flexDirection:"column"}}>
        <div style={{padding:"16px 14px 12px",borderBottom:`1px solid ${T.border}`}}>
          <div style={{fontSize:10,letterSpacing:1.2,textTransform:"uppercase",color:T.orange,fontWeight:700,marginBottom:4}}>
            Class 9 · Maths
          </div>
          <div style={{fontSize:13,fontWeight:800,color:T.textPri,lineHeight:1.3}}>Chapter 2</div>
          <div style={{fontSize:11,color:T.textMute,marginTop:3}}>Linear Polynomials</div>
        </div>
        <nav style={{padding:"8px 6px",flex:1,overflowY:"auto"}}>
          {NAV.map(item=>(
            <button key={item.id} onClick={()=>go(item.id)}
              style={{display:"flex",alignItems:"center",gap:8,width:"100%",textAlign:"left",
                padding:"8px 10px",borderRadius:7,border:"none",cursor:"pointer",marginBottom:2,
                background:active===item.id?"rgba(251,146,60,0.1)":"transparent",
                borderLeft:active===item.id?`2px solid ${T.orange}`:"2px solid transparent",
                color:active===item.id?T.orange:T.textSec,
                fontSize:13,fontWeight:active===item.id?700:400,transition:"all .15s"}}>
              <span>{item.icon}</span><span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div style={{padding:"10px 14px",borderTop:`1px solid ${T.border}`,fontSize:11,color:T.textMute,lineHeight:1.65}}>
          Interactive graphs included<br/>Tap questions to reveal
        </div>
      </div>

      {/* Main */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        <div style={{flexShrink:0,padding:"12px 32px",background:T.surface,borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:16}}>
          <span style={{fontSize:11,letterSpacing:".8px",textTransform:"uppercase",color:T.orange,fontWeight:700}}>
            Class 9 Maths · Chapter 2
          </span>
          <span style={{fontSize:13,color:T.textMute}}>Introduction to Linear Polynomials</span>
        </div>

        <div ref={mainRef} style={{flex:1,overflowY:"auto",padding:"32px 40px"}}>

          {/* Hero */}
          <div style={{display:"grid",gridTemplateColumns:"1.1fr .9fr",gap:36,alignItems:"start",marginBottom:48}}>
            <div>
              <div style={{fontFamily:"monospace",fontSize:11,letterSpacing:".12em",textTransform:"uppercase",color:T.orange,marginBottom:10}}>
                Introduction to Linear Polynomials
              </div>
              <h1 style={{fontSize:"clamp(26px,3.2vw,38px)",fontWeight:900,color:T.textPri,margin:"0 0 12px",lineHeight:1.1}}>
                Feed it a number. Get a number <span style={{color:T.accent}}>back</span>.
              </h1>
              <p style={{fontSize:14.5,color:T.textSec,lineHeight:1.7,maxWidth:"36ch",margin:"0 0 18px"}}>
                An auto fare, a phone bill, a shrinking recharge balance — all of them are one rule applied over and over. Algebra just writes that rule down once.
              </p>
              <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                <button onClick={()=>go("expressions")} style={{padding:"9px 18px",borderRadius:4,border:"none",background:T.accent,color:"#000",fontWeight:700,fontSize:13.5,cursor:"pointer"}}>
                  Start the chapter
                </button>
                <button onClick={()=>go("practice")} style={{padding:"9px 18px",borderRadius:4,border:`1px solid ${T.border}`,background:"transparent",color:T.textSec,fontSize:13.5,cursor:"pointer"}}>
                  Jump to practice
                </button>
              </div>
            </div>
            <FunctionMachine/>
          </div>

          {/* 01 – Expressions */}
          <div ref={SR("expressions")} style={SS}>
            <SL n={1}/><H2 c="Algebraic expressions: the vocabulary"/>
            <P c={<>Raju buys <Cd>x</Cd> red boxes holding 4 pens each and <Cd>y</Cd> blue boxes holding 5 pencils each, and gets 3 pens free. The total is <Cd>4x + 5y + 3</Cd>. Before anything else, learn to name the parts of an expression like this — questions on terms, coefficients and constants are almost free marks.</>}/>
            <DataTable
              headers={["Word","What it means","In 4x + 5y + 3"]}
              rows={[
                [<strong style={{color:T.textPri}}>Term</strong>,"Each piece joined by + or −","4x, 5y, 3"],
                [<strong style={{color:T.textPri}}>Variable</strong>,"The letter whose value can change","x and y"],
                [<strong style={{color:T.textPri}}>Coefficient</strong>,"The number multiplying a variable","4 and 5"],
                [<strong style={{color:T.textPri}}>Constant</strong>,"The term with no variable in it","3"],
              ]}
            />
            <Callout tag="Think of it like this">
              A shopping bill. Each line item is a term. The rate per kilo is the coefficient, the quantity is the variable, and the fixed delivery charge is the constant — it stays the same no matter how much you buy.
            </Callout>
            <P c={<>Some expressions carry more than one variable. Fencing a garden of length <Cd>l</Cd> and width <Cd>w</Cd> gives a total cost of <Cd>200l + 160w + 50lw</Cd> — two variables, and one term where they are multiplied together. From Topic 02 onwards this chapter sticks to expressions with a <strong style={{color:T.textPri}}>single variable</strong>.</>}/>
          </div>

          {/* 02 – Degree */}
          <div ref={SR("degree")} style={SS}>
            <SL n={2}/><H2 c="Polynomials and their degree"/>
            <P c={<>An algebraic expression in <strong style={{color:T.textPri}}>one variable and its powers</strong> is called a <strong style={{color:T.textPri}}>polynomial</strong>. The <strong style={{color:T.textPri}}>degree</strong> is the highest power of the variable appearing in it.</>}/>
            <DataTable
              headers={["Degree","Name","Example"]}
              rows={[
                ["0","Constant polynomial","8  (same as 8x⁰)"],
                ["1",<strong style={{color:T.textPri}}>Linear polynomial</strong>,"3z + 7"],
                ["2","Quadratic polynomial","x² + 5x + 1"],
                ["3","Cubic polynomial","5y³ + y² + 2y − 1"],
              ]}
            />
            <Callout type="trick" tag="Trick to remember">
              Read the degree off the tallest power, not the first term you see. In <Cd>7 − 4x + x³</Cd> the degree is 3, even though the cubic term is written last. Scan the whole expression before answering.
            </Callout>
            <Callout type="warn" tag="Exam alert">
              "What is the coefficient of z in <Cd>4z³ + 5z² − 11</Cd>?" There is no z term at all, so the coefficient is <strong>0</strong> — not "none" and not 5. A missing term has coefficient zero.
            </Callout>
            <WorkedEx
              title="Worked example"
              question="In the polynomial x⁴ − 3x³ + 6x² − 2x + 7, state the degree, the coefficients of x² and x³, and the constant term."
              steps={[
                "Highest power present is 4, so the degree is 4.",
                <span key="2">The x² term is <Cd>+6x²</Cd>, so its coefficient is 6.</span>,
                <span key="3">The x³ term is <Cd>−3x³</Cd>. Carry the minus sign: the coefficient is −3.</span>,
                "The term without a variable is 7.",
              ]}
              answer={<span>Degree <Cd>4</Cd>; coefficient of x² is <Cd>6</Cd>; coefficient of x³ is <Cd>−3</Cd>; constant term <Cd>7</Cd>.</span>}
            />
          </div>

          {/* 03 – Linear */}
          <div ref={SR("linear")} style={SS}>
            <SL n={3}/><H2 c="Linear polynomials and linear equations"/>
            <P c={<>A polynomial of degree 1 is a <strong style={{color:T.textPri}}>linear polynomial</strong>. The perimeter of a square of side <Cd>x</Cd> is <Cd>4x</Cd>. A chess club charging ₹200 to join plus ₹50 per match gives <Cd>200 + 50m</Cd>. Both are linear.</>}/>
            <P c={<>Set a linear polynomial <em>equal</em> to a value and you have a <strong style={{color:T.textPri}}>linear equation</strong>, which you can solve for the unknown.</>}/>
            <Callout tag="Polynomial vs equation">
              <Cd>2x + 10</Cd> is a polynomial — it has no equals sign and nothing to solve. <Cd>2x + 10 = 64</Cd> is an equation, and it has exactly one answer. Exam questions use both words deliberately, so read carefully.
            </Callout>
            <WorkedEx
              title="Worked example — turning words into an equation"
              question="The sum of two numbers is 64. One number is 10 more than the other. Find the two numbers."
              steps={[
                <span key="1">Let the smaller number be <Cd>x</Cd>. Then the larger is <Cd>x + 10</Cd>.</span>,
                <span key="2">Their sum is 64, so <Cd>x + (x + 10) = 64</Cd>.</span>,
                <span key="3">Simplify the left side: <Cd>2x + 10 = 64</Cd>.</span>,
                <span key="4">Subtract 10: <Cd>2x = 54</Cd>, so <Cd>x = 27</Cd>.</span>,
              ]}
              answer={<span>The numbers are <Cd>27 and 37</Cd>. Always check: 27 + 37 = 64. ✓</span>}
            />
            <Callout type="trick" tag="Word-problem method">
              Give the <em>smallest</em> or <em>simplest</em> quantity the name x, and write everything else in terms of it. If the mother is three times as old as Salil, let Salil be x and the mother 3x — never the other way round, or you will be dividing when you could be multiplying.
            </Callout>
            <H3 c="A polynomial as a machine"/>
            <P c={<>Feed a value of x into <Cd>2x + 3</Cd> and one specific number comes out. Put in 4, get <Cd>2 × 4 + 3 = 11</Cd>. Put in −6, get <Cd>2 × (−6) + 3 = −9</Cd>. This input–output view is what mathematicians call a <strong style={{color:T.textPri}}>function</strong>, and the machine at the top of this page is exactly this idea.</>}/>
            <Callout type="warn" tag="Exam alert">
              When substituting a negative value, put it in brackets first. Writing <Cd>2 × −6 + 3</Cd> in a hurry invites a sign slip; <Cd>2(−6) + 3</Cd> does not.
            </Callout>
          </div>

          {/* 04 – Patterns */}
          <div ref={SR("patterns")} style={SS}>
            <SL n={4}/><H2 c="Linear patterns and the nth term"/>
            <P c={<>A <strong style={{color:T.textPri}}>linear pattern</strong> is a sequence in which the difference between consecutive terms is always the same constant. That constant difference is the fingerprint of a linear polynomial.</>}/>
            <P c="Look at a growing pattern of square tiles: 1, 3, 5, 7, 9, 11, 13 …"/>
            <DataTable
              headers={["Stage n","1","2","3","4","5","6","7"]}
              rows={[
                [<strong>Tiles</strong>,"1","3","5","7","9","11","13"],
                [<strong>Difference</strong>,"—","+2","+2","+2","+2","+2","+2"],
              ]}
            />
            <P c={<>The difference is a constant 2, so the rule must be linear. Each stage holds one less than twice the stage number, giving the <strong style={{color:T.textPri}}>nth term 2n − 1</strong>.</>}/>
            <Callout type="trick" tag="Finding the nth term fast">
              The constant difference is the coefficient of n. Here the difference is 2, so start with <Cd>2n</Cd>. Now test n = 1: that gives 2, but the real first term is 1, so you must subtract 1. Rule: <Cd>2n − 1</Cd>. Difference first, then adjust with the first term — it works every time for a linear pattern.
            </Callout>
            <WorkedEx
              title="Worked example — both directions"
              question="Using 2n − 1, how many tiles are in Stage 15? And which stage holds 47 tiles?"
              steps={[
                <span key="1">Forward, substitute n = 15: <Cd>2(15) − 1 = 29</Cd> tiles.</span>,
                <span key="2">Backward, set the rule equal to 47: <Cd>2n − 1 = 47</Cd>.</span>,
                <span key="3">Solve: <Cd>2n = 48</Cd>, so <Cd>n = 24</Cd>.</span>,
              ]}
              answer={<span>Stage 15 has <Cd>29 tiles</Cd>; <Cd>47 tiles</Cd> appear at <Cd>Stage 24</Cd>.</span>}
            />
            <Callout type="warn" tag="Exam alert">
              If solving backwards gives a fraction or a negative, the answer is that no such stage exists. Say so and justify it — for instance, 2n − 1 is always odd, so no stage can hold 200 tiles.
            </Callout>
          </div>

          {/* 05 – Growth & Decay */}
          <div ref={SR("growth")} style={SS}>
            <SL n={5}/><H2 c="Linear growth and linear decay"/>
            <P c="The same rule describes things that build up and things that run down. The only difference is the sign in front of the changing term."/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,margin:"18px 0"}}>
              <div>
                <H3 c="Linear growth"/>
                <P c={<>A quantity increases by a fixed amount over equal intervals. A journey costing ₹100 plus ₹60 per km gives <Cd>C(d) = 100 + 60d</Cd>.</>}/>
                <Formula>start + rate × time</Formula>
                <P c="Every extra kilometre adds exactly ₹60."/>
              </div>
              <div>
                <H3 c="Linear decay"/>
                <P c={<>A quantity decreases by a fixed amount over equal intervals. Water 3 m deep dropping 0.5 m a month gives <Cd>h(t) = 3 − 0.5t</Cd>.</>}/>
                <Formula>start − rate × time</Formula>
                <P c="Every extra month removes exactly 0.5 m."/>
              </div>
            </div>
            <Callout tag="Reading the words">
              "Starts at", "joining fee", "initially", "already has" → that is the constant. "Every", "per", "each" → that is the coefficient. Set up <Cd>start ± rate × n</Cd> and the answer usually falls out in one line.
            </Callout>
            <WorkedEx
              title="Worked example — decay, both directions"
              question="A recharge of ₹600 loses ₹15 of balance each day. Write the balance b(x) after x days, and find when it runs out."
              steps={[
                <span key="1">Start is 600 and the daily loss is 15, so <Cd>b(x) = 600 − 15x</Cd>.</span>,
                "It is decay because the balance falls by the same ₹15 every day — the coefficient of x is negative.",
                <span key="3">The balance runs out when <Cd>600 − 15x = 0</Cd>.</span>,
                <span key="4"><Cd>15x = 600</Cd>, so <Cd>x = 40</Cd>.</span>,
              ]}
              answer={<span>The balance is exhausted after <Cd>40 days</Cd>.</span>}
            />
          </div>

          {/* 06 – Relationship */}
          <div ref={SR("relationship")} style={SS}>
            <SL n={6}/><H2 c="Finding the relationship y = ax + b"/>
            <P c={<>A linear relationship between two variables is written <Cd>y = ax + b</Cd>. Sometimes the rule is handed to you; more often an exam gives you two pieces of data and asks you to build the rule yourself.</>}/>
            <WorkedEx
              title="Worked example — the standard 3-mark question"
              question="A telecom company charges a fixed monthly fee plus a cost per GB. Using 10 GB gives a bill of ₹350; using 20 GB gives ₹550. If y = ax + b, find a and b."
              steps={[
                <span key="1">Substitute the first pair (x = 10, y = 350): <Cd>350 = 10a + b</Cd>.</span>,
                <span key="2">Substitute the second pair (x = 20, y = 550): <Cd>550 = 20a + b</Cd>.</span>,
                <span key="3">From the first equation, <Cd>b = 350 − 10a</Cd>.</span>,
                <span key="4">Put that into the second: <Cd>550 = 20a + (350 − 10a) = 10a + 350</Cd>.</span>,
                <span key="5">So <Cd>10a = 200</Cd> and <Cd>a = 20</Cd>.</span>,
                <span key="6">Then <Cd>b = 350 − 10(20) = 150</Cd>.</span>,
              ]}
              answer={<span>The relationship is <Cd>y = 20x + 150</Cd> — ₹20 per GB, plus a fixed monthly fee of ₹150.</span>}
            />
            <Callout type="trick" tag="Faster route">
              Subtract the two equations instead of substituting. <Cd>(20a + b) − (10a + b) = 550 − 350</Cd> kills b immediately, leaving <Cd>10a = 200</Cd>. One line instead of three — and you can always find b afterwards by putting a back into either equation.
            </Callout>
            <Callout tag="What a and b mean">
              Always finish by saying what the numbers represent. In <Cd>y = 20x + 150</Cd>, the <strong>a</strong> is the rate per unit and the <strong>b</strong> is the fixed charge that applies even when x is zero. Many mark schemes give a mark for this interpretation.
            </Callout>
          </div>

          {/* 07 – Graphs */}
          <div ref={SR("graphs")} style={SS}>
            <SL n={7}/><H2 c="Visualising it: slope and y-intercept"/>
            <P c={<>Every equation of the form <Cd>y = ax + b</Cd> draws a straight line. To plot one you only need two points — pick two easy values of x, work out y, mark them, and join with a ruler.</>}/>
            <P c="Move the two sliders below. The dashed grey line is y = x, kept as a reference so you can judge steepness."/>
            <GraphExplorer/>
            <H3 c="The three rules the graph teaches you"/>
            <ul style={{paddingLeft:20,maxWidth:"64ch",margin:"8px 0",color:T.textSec,fontSize:14.5,lineHeight:1.8}}>
              <li><strong style={{color:T.textPri}}>b is the y-intercept.</strong> The line always crosses the y-axis at <Cd>(0, b)</Cd>. Set b = −2 and the crossing point moves to <Cd>(0, −2)</Cd>.</li>
              <li><strong style={{color:T.textPri}}>a is the slope.</strong> Bigger a means steeper. When <Cd>a &gt; 1</Cd> the line is steeper than <Cd>y = x</Cd>; when <Cd>a &lt; 1</Cd> it is gentler. Negative a tilts the line downwards.</li>
              <li><strong style={{color:T.textPri}}>Same a, different b means parallel.</strong> Change only b and the line slides up or down without tilting — so <Cd>y = 2x − 1</Cd>, <Cd>y = 2x + 1</Cd> and <Cd>y = 2x + 5</Cd> are three parallel lines.</li>
            </ul>
            <Callout tag="The link back to patterns">
              Positive slope draws linear growth; negative slope draws linear decay. And the slope is exactly the constant difference from Topic 04 — for the tile pattern <Cd>y = 2x − 1</Cd>, the slope 2 is the same 2 that the tiles increased by at every stage.
            </Callout>
            <Callout type="warn" tag="Exam alert">
              An equation must be rearranged into <Cd>y = ax + b</Cd> form before you read off a and b. For <Cd>2y = 4x + 7</Cd>, divide throughout by 2 first to get <Cd>y = 2x + 3.5</Cd> — so the slope is 2, not 4.
            </Callout>
            <WorkedEx
              title="Worked example — building a polynomial from its graph"
              question="The graph of a linear polynomial p(x) passes through (1, 5) and (3, 11). Find p(x) and where it cuts the axes."
              steps={[
                <span key="1">Write <Cd>p(x) = ax + b</Cd>. From (1, 5): <Cd>a + b = 5</Cd>. From (3, 11): <Cd>3a + b = 11</Cd>.</span>,
                <span key="2">Subtract the first from the second: <Cd>2a = 6</Cd>, so <Cd>a = 3</Cd>.</span>,
                <span key="3">Then <Cd>b = 5 − 3 = 2</Cd>, giving <Cd>p(x) = 3x + 2</Cd>.</span>,
                <span key="4">It cuts the y-axis at <Cd>(0, 2)</Cd> — just read off b.</span>,
                <span key="5">It cuts the x-axis where y = 0: <Cd>3x + 2 = 0</Cd>, so <Cd>x = −2/3</Cd>.</span>,
              ]}
              answer={<span><Cd>p(x) = 3x + 2</Cd>, cutting the axes at <Cd>(0, 2)</Cd> and <Cd>(−2/3, 0)</Cd>.</span>}
            />
          </div>

          {/* 08 – Mistakes */}
          <div ref={SR("mistakes")} style={SS}>
            <SL n={8}/><H2 c="Where marks are usually lost"/>
            <DataTable
              headers={["Mistake","Fix it like this"]}
              rows={[
                ["Dropping the minus sign of a coefficient",<span>In <Cd>−3x³</Cd> the coefficient is −3. The sign belongs to the number.</span>],
                ["Saying a missing term has 'no' coefficient",<span>It has coefficient <strong>0</strong>. Write 0, not a dash.</span>],
                [<span>Reading slope off <Cd>2y = 4x + 7</Cd> as 4</span>,"Rearrange to y = ax + b first. Slope is 2."],
                ["Sign errors when substituting negatives",<span>Bracket the value: <Cd>2(−6) + 3</Cd>.</span>],
                ["Confusing the constant with the rate",<span>Fixed fee = b; per-unit charge = a. Test with x = 0 to check.</span>],
                ["Answering only half a word problem","If it asks for both numbers, give both. Re-read the question after solving."],
                ["Plotting one point and drawing a line","Two points minimum, three to be safe. Label the axes and the line."],
                ["Not stating units or currency","₹, cm, days, tiles. Units carry marks."],
              ]}
            />
          </div>

          {/* 09 – Practice */}
          <div ref={SR("practice")} style={SS}>
            <SL n={9}/><H2 c="Exam-style practice"/>
            <P c="Attempt each question on paper first, then tap to check the full solution."/>
            <div style={{display:"flex",flexWrap:"wrap",gap:8,margin:"8px 0 20px"}}>
              {["1 mark · 5 Q","2 marks · 4 Q","3 marks · 4 Q","Case study · 1 Q"].map(p=>(
                <span key={p} style={{fontFamily:"monospace",fontSize:11,letterSpacing:".08em",textTransform:"uppercase",
                  color:T.textMute,border:`1px solid ${T.border}`,padding:"3px 9px",borderRadius:99}}>{p}</span>
              ))}
            </div>

            <H3 c="Section A — very short answer"/>
            {[
              {no:"Q1",marks:1,question:"Find the degree of the polynomial 2x² − 5x + 3.",
                solution:<>The highest power of x present is 2, so the degree is <Cd>2</Cd>. It is a quadratic polynomial.</>},
              {no:"Q2",marks:1,question:"What is the coefficient of z in the polynomial 4z³ + 5z² − 11?",
                solution:<>There is no term in z on its own, which means the term is absent. Its coefficient is <Cd>0</Cd>.</>},
              {no:"Q3",marks:1,question:"Write the constant term of 9x³ + 5x² − 8x − 10.",
                solution:<>The term carrying no variable is −10, so the constant term is <Cd>−10</Cd>. Keep the minus sign.</>},
              {no:"Q4",marks:1,question:"State the degree of the polynomial −9 and name its type.",
                solution:<>−9 can be written as <Cd>−9x⁰</Cd>, so the power of the variable is 0. Degree <Cd>0</Cd>, a <strong>constant polynomial</strong>.</>},
              {no:"Q5",marks:1,question:"Where does the line y = 3x − 2 cut the y-axis?",
                solution:<>Comparing with <Cd>y = ax + b</Cd>, here b = −2. The line cuts the y-axis at <Cd>(0, −2)</Cd>, that is 2 units below the origin.</>},
            ].map(q=><PracticeQ key={q.no} {...q}/>)}

            <H3 c="Section B — short answer"/>
            {[
              {no:"Q6",marks:2,question:"Find the value of the linear polynomial 5x − 3 when x = 0, x = −1 and x = 2.",
                solution:<div style={{fontSize:13.5,lineHeight:1.8}}>
                  <p style={{margin:"4px 0"}}><Cd>x = 0 → 5(0) − 3 = −3</Cd></p>
                  <p style={{margin:"4px 0"}}><Cd>x = −1 → 5(−1) − 3 = −5 − 3 = −8</Cd></p>
                  <p style={{margin:"4px 0"}}><Cd>x = 2 → 5(2) − 3 = 10 − 3 = 7</Cd></p>
                </div>},
              {no:"Q7",marks:2,question:"Find the value of the quadratic polynomial 7s² − 4s + 6 when s = −3.",
                solution:<div style={{fontSize:13.5,lineHeight:1.8}}>
                  <p style={{margin:"4px 0"}}><Cd>7(−3)² − 4(−3) + 6</Cd></p>
                  <p style={{margin:"4px 0"}}><Cd>= 7(9) + 12 + 6 = 63 + 12 + 6 = 81</Cd></p>
                  <p style={{margin:"4px 0"}}>Note that squaring the negative makes it positive, while −4 × −3 gives +12.</p>
                </div>},
              {no:"Q8",marks:2,question:"Bela has ₹100 pocket money and spends ₹5 every day. Write the amount left after n days, and find when she is left with ₹40.",
                solution:<div style={{fontSize:13.5,lineHeight:1.8}}>
                  <p style={{margin:"4px 0"}}>Amount left after n days = <Cd>₹(100 − 5n)</Cd>.</p>
                  <p style={{margin:"4px 0"}}><Cd>100 − 5n = 40 → 5n = 60 → n = 12</Cd>.</p>
                  <p style={{margin:"4px 0"}}>She is left with ₹40 on the <Cd>12th day</Cd>.</p>
                </div>},
              {no:"Q9",marks:2,question:"A tile pattern follows 1, 3, 5, 7, … Find the nth term, the tiles at Stage 15, and the stage holding 47 tiles.",
                solution:<div style={{fontSize:13.5,lineHeight:1.8}}>
                  <p style={{margin:"4px 0"}}>The constant difference is 2, and testing n = 1 needs a correction of −1, so the nth term is <Cd>2n − 1</Cd>.</p>
                  <p style={{margin:"4px 0"}}>Stage 15: <Cd>2(15) − 1 = 29 tiles</Cd>.</p>
                  <p style={{margin:"4px 0"}}>For 47 tiles: <Cd>2n − 1 = 47 → n = 24</Cd>, so Stage 24.</p>
                </div>},
            ].map(q=><PracticeQ key={q.no} {...q}/>)}

            <H3 c="Section C — long answer"/>
            {[
              {no:"Q10",marks:3,question:"Salil's mother is three times his age. After 5 years their ages add to 70. Find both present ages.",
                solution:<div style={{fontSize:13.5,lineHeight:1.8}}>
                  <p style={{margin:"4px 0"}}>Let Salil's present age be <Cd>x</Cd>, so his mother's is <Cd>3x</Cd>.</p>
                  <p style={{margin:"4px 0"}}>After 5 years: <Cd>(x + 5) + (3x + 5) = 70</Cd></p>
                  <p style={{margin:"4px 0"}}><Cd>4x + 10 = 70 → 4x = 60 → x = 15</Cd></p>
                  <p style={{margin:"4px 0"}}>Salil is <Cd>15 years</Cd> and his mother is <Cd>45 years</Cd>. Check: 20 + 50 = 70 ✓</p>
                </div>},
              {no:"Q11",marks:3,question:"Ruby has 3 times as many two-rupee coins as five-rupee coins, worth ₹88 in total. How many of each does she have?",
                solution:<div style={{fontSize:13.5,lineHeight:1.8}}>
                  <p style={{margin:"4px 0"}}>Let the number of five-rupee coins be <Cd>x</Cd>. Then two-rupee coins number <Cd>3x</Cd>.</p>
                  <p style={{margin:"4px 0"}}>Total value: <Cd>5(x) + 2(3x) = 88 → 11x = 88 → x = 8</Cd></p>
                  <p style={{margin:"4px 0"}}><Cd>8 five-rupee coins</Cd> and <Cd>24 two-rupee coins</Cd>. Check: ₹40 + ₹48 = ₹88 ✓</p>
                </div>},
              {no:"Q12",marks:3,question:"The length of a rectangle is three more than twice its width, and its perimeter is 24 cm. Find its dimensions.",
                solution:<div style={{fontSize:13.5,lineHeight:1.8}}>
                  <p style={{margin:"4px 0"}}>Let the width be <Cd>w</Cd>, so the length is <Cd>2w + 3</Cd>.</p>
                  <p style={{margin:"4px 0"}}>Perimeter: <Cd>2(w + 2w + 3) = 24 → 3w + 3 = 12 → w = 3</Cd></p>
                  <p style={{margin:"4px 0"}}>Width <Cd>3 cm</Cd>, length <Cd>9 cm</Cd>. Check: 2(3 + 9) = 24 cm ✓</p>
                </div>},
              {no:"Q13",marks:3,question:"Find the slopes and y-intercepts of y = −3x + 4, 2y = 4x + 7, 5y = 6x − 10 and 3y = 6x − 11. Are any of the lines parallel?",
                solution:<div style={{fontSize:13.5,lineHeight:1.8}}>
                  <p style={{margin:"4px 0"}}>Rearrange each into <Cd>y = ax + b</Cd>:</p>
                  <p style={{margin:"4px 0"}}><Cd>y = −3x + 4</Cd> → slope −3, intercept 4</p>
                  <p style={{margin:"4px 0"}}><Cd>y = 2x + 3.5</Cd> → slope 2, intercept 3.5</p>
                  <p style={{margin:"4px 0"}}><Cd>y = 1.2x − 2</Cd> → slope 1.2, intercept −2</p>
                  <p style={{margin:"4px 0"}}><Cd>y = 2x − 11/3</Cd> → slope 2, intercept −11/3</p>
                  <p style={{margin:"4px 0"}}>The 2nd and 4th lines share slope 2 with different intercepts → <strong>parallel</strong>.</p>
                </div>},
            ].map(q=><PracticeQ key={q.no} {...q}/>)}

            <H3 c="Section D — case study"/>
            <PracticeQ no="Q14" marks={4}
              question="An auto fare is ₹25 for the first 2 km, then rises by ₹15 per km. (i) Fare for 10 km. (ii) Linear expression for fare over n km (n ≥ 2). (iii) km for fare = ₹130. (iv) Growth or decay? What does the slope mean?"
              solution={<div style={{fontSize:13.5,lineHeight:1.8}}>
                <p style={{margin:"4px 0"}}><strong>(i)</strong> Beyond the first 2 km there are 8 chargeable km, so fare = <Cd>25 + 15 × 8 = ₹145</Cd>.</p>
                <p style={{margin:"4px 0"}}><strong>(ii)</strong> Fare = <Cd>25 + 15(n − 2) = 15n − 5</Cd> rupees.</p>
                <p style={{margin:"4px 0"}}><strong>(iii)</strong> <Cd>15n − 5 = 130 → 15n = 135 → n = 9 km</Cd>.</p>
                <p style={{margin:"4px 0"}}><strong>(iv)</strong> Linear <strong>growth</strong>. Slope = <Cd>15</Cd> = cost of one extra kilometre.</p>
              </div>}
            />
          </div>

          {/* 10 – Summary */}
          <div ref={SR("summary")} style={{paddingBottom:48}}>
            <SL n={10}/><H2 c="One-page revision sheet"/>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:16}}>
              <div>
                <H3 c="Definitions"/>
                <ul style={{paddingLeft:20,color:T.textSec,fontSize:14,lineHeight:1.9}}>
                  <li>An <strong style={{color:T.textPri}}>algebraic expression</strong> combines numbers, variables and operations. Its pieces are <strong style={{color:T.textPri}}>terms</strong>; the numbers multiplying variables are <strong style={{color:T.textPri}}>coefficients</strong>.</li>
                  <li>A <strong style={{color:T.textPri}}>univariate polynomial</strong> involves one variable and its powers.</li>
                  <li><strong style={{color:T.textPri}}>Degree</strong> = highest power. Degree 0 constant, 1 linear, 2 quadratic, 3 cubic.</li>
                  <li>A <strong style={{color:T.textPri}}>linear pattern</strong> is a sequence whose consecutive differences are constant.</li>
                  <li><strong style={{color:T.textPri}}>Linear growth</strong> rises by a fixed amount each interval; <strong style={{color:T.textPri}}>linear decay</strong> falls by a fixed amount.</li>
                </ul>
              </div>
              <div>
                <H3 c="Facts to recall instantly"/>
                <ul style={{paddingLeft:20,color:T.textSec,fontSize:14,lineHeight:1.9}}>
                  <li>In <Cd>y = ax + b</Cd>: <strong style={{color:T.textPri}}>a</strong> is the slope, <strong style={{color:T.textPri}}>b</strong> is the y-intercept.</li>
                  <li>The line always crosses the y-axis at <Cd>(0, b)</Cd>.</li>
                  <li>When <Cd>b = 0</Cd> the line becomes <Cd>y = ax</Cd> and passes through the origin.</li>
                  <li>Positive slope = growth (rising); negative slope = decay (falling).</li>
                  <li>Equal slopes with different intercepts = <strong style={{color:T.textPri}}>parallel lines</strong>.</li>
                  <li>Slope = the constant difference of the matching linear pattern.</li>
                </ul>
              </div>
            </div>
            <Formula>
              Linear relationship: <span style={{color:T.yellow}}>y = ax + b</span>
              &nbsp;·&nbsp;
              nth term: <span style={{color:T.yellow}}>(common difference)n + adjustment</span>
              &nbsp;·&nbsp;
              Model: <span style={{color:T.yellow}}>start ± rate × n</span>
            </Formula>
          </div>

        </div>
      </div>
    </div>
  );
}
